import { useState, useEffect } from "react";
import { Wind, AlertTriangle, Info } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const WindAlert = () => {
  const [windData, setWindData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);

  const fetchWindData = async () => {
    try {
      const response = await axios.get(`${API}/weather/wind-alerts`);
      setWindData(response.data);
    } catch (error) {
      console.error("Error fetching wind data:", error);
      setWindData({ success: false, alert_level: "unknown", current_speed: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWindData();
    // Refresh every 10 minutes
    const interval = setInterval(fetchWindData, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const getAlertStyle = (level) => {
    switch (level) {
      case "extreme":
        return "bg-red-600/30 text-red-400 border-red-500/50 animate-pulse";
      case "severe":
        return "bg-orange-500/30 text-orange-400 border-orange-500/50";
      case "moderate":
        return "bg-yellow-500/30 text-yellow-400 border-yellow-500/50";
      case "light":
        return "bg-blue-500/30 text-blue-400 border-blue-500/50";
      case "calm":
        return "bg-emerald-500/20 text-emerald-400 border-emerald-500/30";
      default:
        return "bg-zinc-500/20 text-zinc-400 border-zinc-500/30";
    }
  };

  const getAlertIcon = (level) => {
    if (level === "extreme" || level === "severe") {
      return <AlertTriangle className="w-3 h-3 mr-1" />;
    }
    return <Wind className="w-3 h-3 mr-1" />;
  };

  const getAlertText = (level, speed) => {
    switch (level) {
      case "extreme":
        return `VIENTO ${speed}km/h`;
      case "severe":
        return `Viento ${speed}km/h`;
      case "moderate":
        return `Viento ${speed}km/h`;
      case "light":
        return `${speed}km/h`;
      case "calm":
        return "CALMA";
      default:
        return "---";
    }
  };

  const getSpeedColor = (speed) => {
    if (speed >= 90) return "text-red-500";
    if (speed >= 70) return "text-orange-500";
    if (speed >= 50) return "text-yellow-500";
    if (speed >= 30) return "text-blue-400";
    return "text-emerald-400";
  };

  const getBeaufortScale = (speed) => {
    if (speed >= 118) return { scale: 12, desc: "Huracán" };
    if (speed >= 103) return { scale: 11, desc: "Borrasca muy dura" };
    if (speed >= 89) return { scale: 10, desc: "Temporal" };
    if (speed >= 75) return { scale: 9, desc: "Temporal fuerte" };
    if (speed >= 62) return { scale: 8, desc: "Temporal" };
    if (speed >= 50) return { scale: 7, desc: "Viento fuerte" };
    if (speed >= 39) return { scale: 6, desc: "Viento fresco" };
    if (speed >= 29) return { scale: 5, desc: "Brisa fresca" };
    if (speed >= 20) return { scale: 4, desc: "Brisa moderada" };
    if (speed >= 12) return { scale: 3, desc: "Brisa suave" };
    if (speed >= 6) return { scale: 2, desc: "Brisa ligera" };
    if (speed >= 1) return { scale: 1, desc: "Ventolina" };
    return { scale: 0, desc: "Calma" };
  };

  if (loading) {
    return (
      <Badge variant="outline" className="bg-zinc-500/20 text-zinc-400 border-zinc-500/30 text-xs py-1 px-2 h-7">
        <Wind className="w-3 h-3 mr-1 animate-pulse" />
        AEMET...
      </Badge>
    );
  }

  const alertLevel = windData?.alert_level || "unknown";
  const currentSpeed = windData?.current_speed || 0;
  const maxGust = windData?.max_gust || 0;

  return (
    <>
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge 
              variant="outline" 
              className={`${getAlertStyle(alertLevel)} text-xs py-1 px-2 h-7 cursor-pointer`}
              onClick={() => setShowDetails(true)}
              data-testid="wind-alert-badge"
            >
              {getAlertIcon(alertLevel)}
              {getAlertText(alertLevel, currentSpeed)}
            </Badge>
          </TooltipTrigger>
          <TooltipContent className="bg-zinc-800 text-zinc-100 border-zinc-700">
            <p>Alerta de viento - La Aldea</p>
            {maxGust > currentSpeed && (
              <p className="text-xs text-zinc-400">
                Rachas de hasta {maxGust} km/h
              </p>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Details Dialog */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 flex items-center gap-2">
              <Wind className="w-5 h-5 text-cyan-400" />
              Condiciones de Viento - AEMET
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Current conditions */}
            <div className="bg-zinc-800/50 rounded-lg p-4 text-center">
              <div className="flex items-center justify-center gap-4 mb-3">
                <div>
                  <span className={`text-4xl font-bold ${getSpeedColor(currentSpeed)}`}>
                    {currentSpeed}
                  </span>
                  <span className="text-lg text-zinc-400 ml-1">km/h</span>
                </div>
                <div className="text-left">
                  <p className="text-xs text-zinc-500 uppercase">Escala Beaufort</p>
                  <p className="text-lg font-bold text-zinc-200">{getBeaufortScale(currentSpeed).scale}</p>
                  <p className="text-xs text-zinc-400">{getBeaufortScale(currentSpeed).desc}</p>
                </div>
              </div>
              
              {maxGust > 0 && (
                <div className="flex items-center justify-center gap-2 text-sm">
                  <span className="text-zinc-500">Rachas:</span>
                  <span className={`font-bold ${getSpeedColor(maxGust)}`}>{maxGust} km/h</span>
                </div>
              )}
            </div>

            {/* Direction */}
            {windData?.direction && (
              <div className="bg-zinc-800/30 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-zinc-400 uppercase tracking-wider">Dirección</span>
                  <span className="text-sm font-mono text-zinc-300">{windData.direction}</span>
                </div>
              </div>
            )}

            {/* Forecast */}
            {windData?.forecast && windData.forecast.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-xs text-zinc-400 uppercase tracking-wider">Previsión próximas horas</h4>
                <div className="grid grid-cols-4 gap-2">
                  {windData.forecast.slice(0, 4).map((item, index) => (
                    <div key={index} className="bg-zinc-800/30 rounded-lg p-2 text-center">
                      <p className="text-xs text-zinc-500">{item.hour}</p>
                      <p className={`text-lg font-bold ${getSpeedColor(item.speed)}`}>{item.speed}</p>
                      <p className="text-[10px] text-zinc-500">km/h</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Warning thresholds */}
            <div className="bg-zinc-800/30 rounded-lg p-3">
              <h4 className="text-xs text-zinc-400 uppercase tracking-wider mb-2">Umbrales de alerta</h4>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-emerald-400">Calma:</span>
                  <span className="text-zinc-300">&lt;30 km/h</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-400">Ligero:</span>
                  <span className="text-zinc-300">30-49 km/h</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-yellow-400">Moderado:</span>
                  <span className="text-zinc-300">50-69 km/h</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-orange-400">Fuerte:</span>
                  <span className="text-zinc-300">70-89 km/h</span>
                </div>
                <div className="col-span-2 flex justify-between">
                  <span className="text-red-400">Extremo:</span>
                  <span className="text-zinc-300">&gt;90 km/h</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 text-[10px] text-zinc-500 pt-2 border-t border-zinc-800">
              <Info className="w-3 h-3" />
              <span>Datos: AEMET - La Aldea de San Nicolás</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WindAlert;
